// apps/client/src/services/organisationProfile.ts

import {
  OrganisationProfile,
  OrganisationProfileData,
  OrganisationProfileAIGetResponse,
  OrganisationProfileAIUpdateResponse,
} from '@fsp/types';
import { apiClient } from './apiClient';

/**
 * Fetch organisation AI profile
 */
export const fetchOrganisationProfile = async (
  organisationId: string,
  signal?: AbortSignal
): Promise<OrganisationProfile | null> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/profile-ai`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch organisation profile: ${response.statusText}`);
  }

  const result: OrganisationProfileAIGetResponse = await response.json();
  return result.profile;
};

/**
 * Update organisation AI profile
 */
export const updateOrganisationProfile = async (
  organisationId: string,
  profileData: OrganisationProfileData,
  signal?: AbortSignal
): Promise<OrganisationProfile> => {
  const response = await apiClient.put(
    `/organisations/${organisationId}/profile-ai`,
    { profileData },
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to update profile: ${response.statusText}`);
  }

  const result: OrganisationProfileAIUpdateResponse = await response.json();
  return result.profile;
};

/**
 * Generate organisation profile with AI chatbot (streaming)
 */
export async function* generateOrganisationProfileWithAI(
  organisationId: string,
  userMessage: string,
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>
): AsyncGenerator<string> {
  const response = await apiClient.post(
    `/organisations/${organisationId}/profile-ai/generate`,
    {
      userMessage,
      conversationHistory: conversationHistory || [],
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to generate profile: ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  try {
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // Keep the last incomplete line in buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data === '[DONE]') {
            return;
          }

          if (!data) continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.chunk) {
              yield parsed.chunk;
            }
            if (parsed.error) {
              throw new Error(parsed.error);
            }
          } catch (e) {
            // Skip invalid JSON
            console.warn('Failed to parse SSE chunk:', data);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
